import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, CheckCircle2, Loader2, Sparkles, Wand2 } from "lucide-react";
import { DraftCopilotChat } from "@/components/draft/DraftCopilotChat";
import { Button } from "@/components/ui/button";
import { useToast } from "@/contexts/ToastContext";
import {
  formatClauseCategoryLabel,
  formatClauseVariantLabel,
} from "@/lib/clause-variant-labels";
import { parseDraftDocumentModel, stripSectionNumberPrefix } from "@/lib/draft-document-utils";
import {
  findingStatusBadgeClass,
  findingStatusLabel,
  isOpenFinding,
  severityBadgeClass,
  severityLabel,
} from "@/lib/draft-review-utils";
import { cn } from "@/lib/utils";
import {
  applyDraftClauseAlternative,
  fetchDraftClauseAlternatives,
  fetchDraftFindings,
  generateDraftReview,
  updateDraftFinding,
} from "@/lib/api/drafts";
import { useAuth } from "@/contexts/AuthContext";
import type {
  DraftClauseAlternative,
  DraftReviewFinding,
  DraftSection,
  SectionProvenance,
} from "@/types/drafting";

type AssistanceTab = "clauses" | "copilot" | "review";

interface AssistancePanelProps {
  matterId: string;
  contentJson?: unknown;
  sectionProvenance?: SectionProvenance[] | unknown;
  onContentUpdated?: (contentJson?: unknown, versionId?: string) => void;
  className?: string;
}

function parseProvenance(input: SectionProvenance[] | unknown): SectionProvenance[] {
  if (!Array.isArray(input)) return [];
  return input.filter((item): item is SectionProvenance => Boolean(item && typeof item === "object"));
}

function getSelectedAlternativeId(
  sectionKey: string,
  sections: DraftSection[],
  provenance: SectionProvenance[]
): string | undefined {
  const section = sections.find((item) => item.sectionKey === sectionKey);
  if (!section) return undefined;
  const entry = provenance.find((item) => item.sectionId === section.sectionId);
  return entry?.clauseAlternativeId;
}

function groupAlternativesByCategory(alternatives: DraftClauseAlternative[]) {
  const map = new Map<string, DraftClauseAlternative[]>();
  for (const alternative of alternatives) {
    const category = alternative.clause_category ?? "general";
    if (!map.has(category)) map.set(category, []);
    map.get(category)!.push(alternative);
  }
  return map;
}

export function AssistancePanel({
  matterId,
  contentJson,
  sectionProvenance,
  onContentUpdated,
  className,
}: AssistancePanelProps) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canGenerateReview = hasPermission("draft", "regenerate");
  const [tab, setTab] = useState<AssistanceTab>("clauses");
  const [clauses, setClauses] = useState<DraftClauseAlternative[]>([]);
  const [findings, setFindings] = useState<DraftReviewFinding[]>([]);
  const [isLoadingClauses, setIsLoadingClauses] = useState(false);
  const [isLoadingFindings, setIsLoadingFindings] = useState(false);
  const [isGeneratingReview, setIsGeneratingReview] = useState(false);
  const [updatingFindingId, setUpdatingFindingId] = useState<string | null>(null);
  const [isApplyingClause, setIsApplyingClause] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const documentModel = useMemo(() => parseDraftDocumentModel(contentJson), [contentJson]);
  const provenance = useMemo(() => parseProvenance(sectionProvenance), [sectionProvenance]);

  const loadClauses = useCallback(async () => {
    setIsLoadingClauses(true);
    setError(null);
    try {
      const rows = await fetchDraftClauseAlternatives(matterId);
      setClauses(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load clauses");
    } finally {
      setIsLoadingClauses(false);
    }
  }, [matterId]);

  const loadFindings = useCallback(async () => {
    setIsLoadingFindings(true);
    setError(null);
    try {
      const rows = await fetchDraftFindings(matterId);
      setFindings(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load findings");
    } finally {
      setIsLoadingFindings(false);
    }
  }, [matterId]);

  useEffect(() => {
    if (tab === "clauses") void loadClauses();
    if (tab === "review") void loadFindings();
  }, [tab, loadClauses, loadFindings]);

  const groupedClauses = useMemo(() => {
    const map = new Map<string, DraftClauseAlternative[]>();
    for (const clause of clauses) {
      const key = clause.section_key;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(clause);
    }
    return map;
  }, [clauses]);

  const documentSections = useMemo(() => {
    if (documentModel.sections.length) return documentModel.sections;

    return Array.from(groupedClauses.keys()).map((sectionKey) => ({
      sectionId: `sec-${sectionKey}`,
      sectionKey,
      title: sectionKey
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" "),
      content: { type: "doc" as const, content: [] },
    }));
  }, [documentModel.sections, groupedClauses]);

  const sectionsWithAlternatives = useMemo(
    () => documentSections.filter((section) => (groupedClauses.get(section.sectionKey) ?? []).length > 0),
    [documentSections, groupedClauses]
  );

  const sectionsWithoutAlternatives = useMemo(
    () => documentSections.filter((section) => !(groupedClauses.get(section.sectionKey) ?? []).length),
    [documentSections, groupedClauses]
  );

  async function handleSelectClause(
    alternative: DraftClauseAlternative,
    sectionTitle: string
  ) {
    const variantLabel = formatClauseVariantLabel(alternative.posture, alternative.clause_category);
    const selectedId = getSelectedAlternativeId(
      alternative.section_key,
      documentModel.sections,
      provenance
    );

    if (selectedId === alternative.id) {
      showToast(`"${variantLabel}" is already active in ${sectionTitle}.`, "info");
      return;
    }

    setIsApplyingClause(alternative.id);
    setError(null);
    try {
      await applyDraftClauseAlternative(matterId, alternative.id, alternative.section_key);
      onContentUpdated?.();
      showToast(`Updated "${sectionTitle}" to ${variantLabel}.`, "success");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to apply clause";
      setError(message);
      showToast(message, "error");
    } finally {
      setIsApplyingClause(null);
    }
  }

  async function handleFindingAction(finding: DraftReviewFinding, status: string) {
    setUpdatingFindingId(finding.id);
    setError(null);
    try {
      const updated = await updateDraftFinding(matterId, finding.id, { status });
      setFindings((prev) => prev.map((f) => (f.id === finding.id ? updated : f)));
      showToast(`Finding marked as ${findingStatusLabel(status).toLowerCase()}.`, "success");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update finding";
      setError(message);
      showToast(message, "error");
    } finally {
      setUpdatingFindingId(null);
    }
  }

  async function handleGenerateReview() {
    if (isGeneratingReview) return;
    setIsGeneratingReview(true);
    setError(null);
    try {
      const result = await generateDraftReview(matterId);
      setFindings(result.findings);
      showToast("Draft review complete.", "success");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate review";
      setError(message);
      showToast(message, "error");
    } finally {
      setIsGeneratingReview(false);
    }
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col overflow-hidden rounded-xl border shadow-[var(--shadow-card)]",
        tab === "copilot"
          ? "border-[rgba(15,23,42,0.09)] bg-[var(--chat-shell-bg)] dark:border-white/10 dark:bg-gradient-to-b dark:from-slate-800 dark:to-slate-900 dark:shadow-none"
          : "border-[var(--lm-border-card)] bg-white dark:border-slate-700 dark:bg-slate-950/45",
        className
      )}
    >
      <div className="flex shrink-0 border-b border-[var(--lm-border-subtle)] dark:border-slate-700">
        {(
          [
            { id: "clauses", label: "Clauses", icon: Wand2 },
            { id: "copilot", label: "Copilot", icon: Sparkles },
            { id: "review", label: "Review", icon: CheckCircle2 },
          ] as const
        ).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors",
              tab === item.id
                ? "border-b-2 border-primary text-primary"
                : "text-[var(--lm-muted)] hover:text-[var(--lm-foreground-strong)] dark:text-slate-400"
            )}
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {error && tab !== "copilot" ? (
          <p className="mx-4 mt-4 shrink-0 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
            {error}
          </p>
        ) : null}

        {tab === "clauses" ? (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
            <div className="rounded-lg border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40">
              <p className="text-xs leading-relaxed text-[var(--lm-foreground-strong)] dark:text-slate-200">
                Approved clause variants are grouped by document section. Selecting one replaces that
                section&apos;s wording in the draft.
              </p>
              {!isLoadingClauses && documentSections.length > 0 ? (
                <p className="mt-1.5 text-[11px] text-[var(--lm-muted)] dark:text-slate-400">
                  {sectionsWithAlternatives.length} of {documentSections.length} sections have firm-approved
                  alternatives. Other sections can be edited directly or via Copilot.
                </p>
              ) : null}
            </div>

            {isLoadingClauses ? (
              <div className="flex items-center gap-2 text-sm text-[var(--lm-muted)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading clause alternatives...
              </div>
            ) : null}

            {!isLoadingClauses && clauses.length === 0 ? (
              <p className="text-sm text-[var(--lm-muted)] dark:text-slate-400">
                No clause alternatives are configured for this contract type yet. Edit the draft directly
                or use Copilot.
              </p>
            ) : null}

            {sectionsWithAlternatives.map((section) => {
              const alternatives = groupedClauses.get(section.sectionKey) ?? [];
              const categories = groupAlternativesByCategory(alternatives);
              const sectionIndex = documentSections.findIndex(
                (item) => item.sectionKey === section.sectionKey
              );
              const sectionNumber = sectionIndex >= 0 ? sectionIndex + 1 : null;
              const sectionTitle = stripSectionNumberPrefix(section.title);
              const selectedId = getSelectedAlternativeId(
                section.sectionKey,
                documentModel.sections,
                provenance
              );

              return (
                <div key={section.sectionKey}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--lm-foreground-strong)] dark:text-slate-200">
                    {sectionNumber ? `${sectionNumber}. ` : ""}
                    {sectionTitle}
                  </h3>
                  <div className="space-y-3">
                    {Array.from(categories.entries()).map(([category, categoryAlternatives]) => (
                      <div key={`${section.sectionKey}-${category}`}>
                        {categories.size > 1 ? (
                          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-500">
                            {formatClauseCategoryLabel(category)}
                          </p>
                        ) : null}
                        <div className="space-y-2">
                          {categoryAlternatives.map((alt) => {
                            const isSelected = selectedId === alt.id;
                            const isApplying = isApplyingClause === alt.id;
                            const label = formatClauseVariantLabel(alt.posture, alt.clause_category);

                            return (
                              <button
                                key={alt.id}
                                type="button"
                                disabled={Boolean(isApplyingClause)}
                                onClick={() => void handleSelectClause(alt, sectionTitle)}
                                className={cn(
                                  "w-full rounded-lg border px-3 py-2.5 text-left transition-colors",
                                  isSelected
                                    ? "border-amber-500/80 bg-amber-500/5 dark:border-amber-400/70 dark:bg-amber-400/10"
                                    : "border-[var(--lm-border-subtle)] hover:border-[var(--lm-border-card)] dark:border-slate-700 dark:hover:border-slate-600"
                                )}
                              >
                                <div className="mb-1.5 flex items-center justify-between gap-2">
                                  <span className="text-xs font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                                    {label}
                                  </span>
                                  {isSelected ? (
                                    <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-amber-500 bg-amber-500 text-white dark:border-amber-400 dark:bg-amber-400">
                                      <Check className="h-3 w-3" />
                                    </span>
                                  ) : isApplying ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-[var(--lm-muted)]" />
                                  ) : null}
                                </div>
                                <p className="text-xs leading-relaxed text-[var(--lm-muted)] dark:text-slate-400">
                                  {alt.language.slice(0, 200)}
                                  {alt.language.length > 200 ? "…" : ""}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {!isLoadingClauses && sectionsWithoutAlternatives.length > 0 ? (
              <div className="border-t border-[var(--lm-border-subtle)] pt-4 dark:border-slate-700">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-500">
                  Sections without approved variants
                </p>
                <div className="space-y-2">
                  {sectionsWithoutAlternatives.map((section) => {
                    const sectionIndex = documentSections.findIndex(
                      (item) => item.sectionKey === section.sectionKey
                    );
                    const sectionTitle = stripSectionNumberPrefix(section.title);
                    return (
                      <div
                        key={section.sectionKey}
                        className="rounded-lg border border-dashed border-[var(--lm-border-subtle)] px-3 py-2 dark:border-slate-700"
                      >
                        <p className="text-xs font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
                          {sectionIndex >= 0 ? `${sectionIndex + 1}. ` : ""}
                          {sectionTitle}
                        </p>
                        <p className="mt-0.5 text-[11px] text-[var(--lm-muted)] dark:text-slate-500">
                          No firm-approved alternatives — edit in the document or use Copilot.
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === "copilot" ? (
          <DraftCopilotChat
            className="min-h-0 flex-1"
            matterId={matterId}
            onContentUpdated={onContentUpdated}
          />
        ) : null}

        {tab === "review" ? (
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            <div className="rounded-lg border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40">
              <p className="text-xs leading-relaxed text-[var(--lm-foreground-strong)] dark:text-slate-200">
                Run automated quality and policy checks on the current draft. Open the Review step for
                full metrics and export.
              </p>
              {canGenerateReview ? (
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  className="mt-2 gap-2"
                  disabled={isGeneratingReview}
                  onClick={() => void handleGenerateReview()}
                >
                  {isGeneratingReview ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {isGeneratingReview ? "Generating…" : "Generate review"}
                </Button>
              ) : null}
            </div>
            {isLoadingFindings ? (
              <div className="flex items-center gap-2 text-sm text-[var(--lm-muted)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading findings...
              </div>
            ) : null}
            {!isLoadingFindings && findings.length === 0 ? (
              <p className="text-sm text-[var(--lm-muted)] dark:text-slate-400">
                No review findings yet.
              </p>
            ) : null}
            {findings.map((finding) => {
              const isOpen = isOpenFinding(finding);
              const isUpdating = updatingFindingId === finding.id;

              return (
                <div
                  key={finding.id}
                  className={cn(
                    "rounded-lg border border-[var(--lm-border-subtle)] p-3 dark:border-slate-700",
                    !isOpen && "opacity-90"
                  )}
                >
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <span className="text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-white">
                      {finding.title}
                    </span>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                          severityBadgeClass(finding.severity)
                        )}
                      >
                        {severityLabel(finding.severity)}
                      </span>
                      {!isOpen ? (
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-medium capitalize",
                            findingStatusBadgeClass(finding.status)
                          )}
                        >
                          {findingStatusLabel(finding.status)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <p className="mb-2 text-xs leading-relaxed text-[var(--lm-muted)] dark:text-slate-300">
                    {finding.explanation}
                  </p>
                  {finding.proposed_revision ? (
                    <p className="mb-2 text-xs italic text-[var(--lm-foreground-strong)] dark:text-slate-200">
                      {finding.proposed_revision}
                    </p>
                  ) : null}
                  {isOpen ? (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="xs"
                        variant="default"
                        disabled={isUpdating}
                        onClick={() => void handleFindingAction(finding, "accepted")}
                      >
                        {isUpdating ? "Saving…" : "Accept"}
                      </Button>
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        disabled={isUpdating}
                        onClick={() => void handleFindingAction(finding, "dismissed")}
                      >
                        Dismiss
                      </Button>
                    </div>
                  ) : (
                    <p className="text-[11px] text-[var(--lm-muted)] dark:text-slate-500">
                      Recorded as {findingStatusLabel(finding.status).toLowerCase()}.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
